import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  TextField,
  Button,
  Tooltip
} from '@mui/material';
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Refresh as ResetIcon,
  Tune as OffsetIcon,
  CheckCircle as StatusIcon,
  Warning as AlertIcon
} from '@mui/icons-material';
import type { RaspiDevice, Chamber } from '../../types/raspi';
import { useChamberStore } from '../../store/chamberStore';
import { updatePumpTime, calibrateSensors, getArduinoAvailableVersion } from '../../services/api';
import { DeleteButton } from '../DeleteButton';
import { useAuthStore } from '../../store/authStore';
import { useUnauthorizedAction } from '../../App';
import { useTheme } from '@mui/material/styles';
import { useNotifier } from '../NotificationProvider';
import ResetCalibrationDialog from '../dialogs/ResetCalibrationDialog';
import OffsetCalibrationDialog from '../dialogs/OffsetCalibrationDialog';

interface ChamberCardProps {
  selectedRaspi: RaspiDevice | undefined;
  selectedRaspiId: string | null;
  onDataRefresh: () => void;
}

const ChamberCard: React.FC<ChamberCardProps> = ({
  selectedRaspi,
  selectedRaspiId,
}) => {
  const theme = useTheme();
  // Local state for pump control
  const [pumpTime, setPumpTime] = useState(1000);
  
  // Arduino version comparison state
  const [availableArduinoVersion, setAvailableArduinoVersion] = useState<string>("Unknown");
  const [isLatestVersion, setIsLatestVersion] = useState<boolean>(true);
  
  // Alert description visibility state
  const [showAlertDescription, setShowAlertDescription] = useState<boolean>(false);
  
  // Calibration dialog states
  const [showResetDialog, setShowResetDialog] = useState<boolean>(false);
  const [showOffsetDialog, setShowOffsetDialog] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [isCalibrating, setIsCalibrating] = useState<boolean>(false);
  const [isPumping, setIsPumping] = useState<boolean>(false);

  // Any in-flight chamber action — used to disable all chamber action buttons
  const isChamberActionInFlight = isResetting || isCalibrating || isPumping;

  const { allChambers } = useChamberStore();
  const getRole = useAuthStore((state) => state.getRole);
  const isViewer = getRole() === 'viewer' || getRole() === 'public';
  const triggerUnauthorized = useUnauthorizedAction();
  const { runWithNotification, notify } = useNotifier();

  const getSelectedChamber = (): Chamber | null => {
    if (!selectedRaspi?.chamber_id) return null;
    return allChambers[selectedRaspi.chamber_id] || null;
  };

  // Fetch Arduino available version and compare with installed version
  useEffect(() => {
    const fetchArduinoVersion = async () => {
      try {
        const response = await getArduinoAvailableVersion();
        setAvailableArduinoVersion(response.available_version);
      } catch (error) {
        console.error('Error fetching Arduino version:', error);
        setAvailableArduinoVersion("Unknown");
      }
    };

    fetchArduinoVersion();
  }, []);

  // Compare versions when chamber or available version changes
  useEffect(() => {
    const selectedChamber = getSelectedChamber();
    if (selectedChamber?.chamber_software_version && availableArduinoVersion !== "Unknown") {
      const isLatest = selectedChamber.chamber_software_version === availableArduinoVersion;
      setIsLatestVersion(isLatest);
    } else {
      setIsLatestVersion(true); // Default to true if no data
    }
  }, [selectedRaspi, availableArduinoVersion, allChambers]);

  const handlePumpTimeChange = (newTime: number) => {
    setPumpTime(Math.max(50, Math.min(10000, newTime)));
  };

  const handlePumpTimeIncrement = () => {
    handlePumpTimeChange(pumpTime + 50);
  };

  const handlePumpTimeDecrement = () => {
    handlePumpTimeChange(pumpTime - 50);
  };

  const handlePumpTimeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value)) {
      setPumpTime(value);
    }
  };

  const handlePumpTimeBlur = () => {
    // Ensure the value is within bounds when user finishes editing
    handlePumpTimeChange(pumpTime);
  };

  const handlePumpCommand = async (direction: 'positive' | 'negative') => {
    if (!selectedRaspiId || isChamberActionInFlight) return;

    setIsPumping(true);
    try {
      await runWithNotification(
        async () => {
          const success = await updatePumpTime(selectedRaspiId, pumpTime, direction);
          if (!success) throw new Error('Pump command rejected');
        },
        {
          pending: `Running pump ${direction === 'positive' ? '→' : '←'} for ${pumpTime} ms...`,
          success: `Pump ${direction === 'positive' ? '→' : '←'} command sent`,
          error: 'Failed to send pump command',
        }
      );
    } finally {
      setIsPumping(false);
    }
  };

  const handleCalibrateSensors = async (calibrationType: 'reset' | 'offset') => {
    if (!selectedRaspiId || isChamberActionInFlight) return;

    const selectedChamber = getSelectedChamber();

    // For RESET: Check if chamber is in RUN state
    if (calibrationType === 'reset') {
      if (selectedChamber?.chamber_status === 'RUN') {
        notify('Cannot reset calibration during RUN state. Stop the experiment first.', 'error');
        return;
      }
      setShowResetDialog(true);
    } else {
      // For OFFSET: Show confirmation dialog
      setShowOffsetDialog(true);
    }
  };

  const handleConfirmReset = async () => {
    if (!selectedRaspiId || isResetting) return;

    setIsResetting(true);
    try {
      const result = await runWithNotification(
        async () => {
          const success = await calibrateSensors(selectedRaspiId, 'reset');
          if (!success) throw new Error('Reset rejected');
        },
        {
          pending: 'Resetting sensor calibration...',
          success: 'Sensor calibration reset',
          error: 'Failed to reset sensor calibration',
        }
      );
      if (result !== null) {
        setShowResetDialog(false);
      }
    } finally {
      setIsResetting(false);
    }
  };

  const handleCancelReset = () => {
    if (isResetting) return;
    setShowResetDialog(false);
  };

  const handleConfirmOffset = async () => {
    if (!selectedRaspiId || isCalibrating) return;

    setIsCalibrating(true);
    try {
      const result = await runWithNotification(
        async () => {
          const success = await calibrateSensors(selectedRaspiId, 'offset');
          if (!success) throw new Error('Offset calibration rejected');
        },
        {
          pending: 'Running offset calibration...',
          success: 'Offset calibration applied',
          error: 'Failed to apply offset calibration',
        }
      );
      if (result !== null) {
        setShowOffsetDialog(false);
      }
    } finally {
      setIsCalibrating(false);
    }
  };

  const handleCancelOffset = () => {
    if (isCalibrating) return;
    setShowOffsetDialog(false);
  };

  const handleAlertIconClick = () => {
    setShowAlertDescription(!showAlertDescription);
  };

  // Helper functions for status and alert icons/colors
  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RUN':
      case 'RUNNING':
      case 'ACTIVE':
        return <StatusIcon sx={{ color: '#4caf50', fontSize: theme.iconSizes.small }} />;
      case 'PAUSE':
      case 'PAUSED':
      case 'WAITING':
        return <StatusIcon sx={{ color: '#ff9800', fontSize: theme.iconSizes.small }} />;
      case 'STOP':
      case 'STOPPED':
      case 'INACTIVE':
        return <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
      case 'SLEEP':
        return <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
      case 'ERROR':
      case 'DISCONNECTED':
        return <StatusIcon sx={{ color: '#f44336', fontSize: theme.iconSizes.small }} />;
      default:
        return <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RUN':
      case 'RUNNING':
      case 'ACTIVE':
        return '#4caf50'; // Green - chamber is running
      case 'PAUSE':
      case 'PAUSED':
      case 'WAITING':
        return '#ff9800'; // Orange - chamber is paused/waiting
      case 'STOP':
      case 'STOPPED':
      case 'INACTIVE':
        return '#9e9e9e'; // Gray - chamber is stopped
      case 'SLEEP':
        return '#9e9e9e'; // Gray - chamber is sleeping
      case 'ERROR':
      case 'DISCONNECTED':
        return '#f44336'; // Red - chamber has error or is disconnected
      default:
        return '#9e9e9e'; // Gray - unknown status
    }
  };

  const getAlertIcon = (alertLevel: string) => {
    switch (alertLevel?.toUpperCase()) {
      case 'CRITICAL':
      case 'HIGH':
        return <AlertIcon sx={{ color: '#f44336', fontSize: theme.iconSizes.small }} />;
      case 'MEDIUM':
      case 'LOW':
        return <AlertIcon sx={{ color: '#ff9800', fontSize: theme.iconSizes.small }} />;
      case 'NO':
      case 'NONE':
        return <AlertIcon sx={{ color: '#4caf50', fontSize: theme.iconSizes.small }} />;
      default:
        return <AlertIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />;
    }
  };

  const getAlertColor = (alertLevel: string) => {
    switch (alertLevel?.toUpperCase()) {
      case 'CRITICAL':
      case 'HIGH':
        return '#f44336'; // Red - critical alert
      case 'MEDIUM':
      case 'LOW':
        return '#ff9800'; // Orange - warning alert
      case 'NO':
      case 'NONE':
        return '#4caf50'; // Green - no alerts
      default:
        return '#9e9e9e'; // Gray - unknown alert level
    }
  };

  // Instead of inline calculations, create reactive variables:
  const selectedChamber = getSelectedChamber();
  const progressPercentage = selectedChamber && selectedChamber.chamber_meas_total > 0
    ? Math.min(Math.max((selectedChamber.chamber_meas_counter || 0) / selectedChamber.chamber_meas_total * 100, 0), 100)
    : 0;

  const progressText = `${Math.round(progressPercentage)}%`;
  const measurementsText = `MEASUREMENTS: ${selectedChamber?.chamber_meas_counter || 0}/${selectedChamber?.chamber_meas_total || 0}`;

  return (
    <>
    <Card sx={{ backgroundColor: 'white' }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gridTemplateRows: 'auto',
          gap: 1,
          gridTemplateAreas: `
            "chamber_id chamber_details chamber_control chamber_monitor"
          `
        }}>
          {/* Chamber ID Box */}
          <Box sx={{ 
            gridArea: 'chamber_id',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"CHAMBER ID"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
              {/* Chamber ID without border */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 2 }}>
                <Typography variant="entityId" sx={{ color: 'text.primary' }}>
                  {getSelectedChamber()?.chamber_id || 'None'}
                </Typography>
              </Box>
              
              {/* Status and Alert icons in one line */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flex: 1 }}>
                {/* Status */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: getSelectedChamber() ? 
                    (getSelectedChamber()?.chamber_status === 'STOP' ? '#ffebee' : 
                     getSelectedChamber()?.chamber_status === 'SLEEP' ? '#fff3e0' : 'transparent') : 'transparent',
                  border: getSelectedChamber() ? 
                    (getSelectedChamber()?.chamber_status === 'STOP' ? '1px solid #ffcdd2' : 
                     getSelectedChamber()?.chamber_status === 'SLEEP' ? '1px solid #ffe0b2' : 'none') : 'none'
                }}>
                  <Tooltip title={''} arrow>
                    <span>{getSelectedChamber() ? getStatusIcon(getSelectedChamber()?.chamber_status || 'UNKNOWN') : <StatusIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />}</span>
                  </Tooltip>
                  <Typography variant="iconValueText" sx={{ textAlign: 'center', color: getSelectedChamber() ? getStatusColor(getSelectedChamber()?.chamber_status || 'UNKNOWN') : '#9e9e9e' }}>
                    {getSelectedChamber() ? (getSelectedChamber()?.chamber_status || 'UNKNOWN') : 'N/A'}
                  </Typography>
                  <Typography variant="iconLabelText" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    STATUS
                  </Typography>
                </Box>
                
                {/* Alert */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: getSelectedChamber() ? 
                    (getSelectedChamber()?.chamber_alert === 'HIGH' ? '#ffebee' : 
                     getSelectedChamber()?.chamber_alert === 'LOW' ? '#fff3e0' : 'transparent') : 'transparent',
                  border: getSelectedChamber() ? 
                    (getSelectedChamber()?.chamber_alert === 'HIGH' ? '1px solid #ffcdd2' : 
                     getSelectedChamber()?.chamber_alert === 'LOW' ? '1px solid #ffe0b2' : 'none') : 'none'
                }}>
                  <Tooltip title={getSelectedChamber()?.chamber_alert_description || ''} arrow>
                    <span 
                      onClick={handleAlertIconClick}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'opacity 0.2s ease',
                        opacity: 1
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      {getSelectedChamber() ? getAlertIcon(getSelectedChamber()?.chamber_alert || 'NO') : <AlertIcon sx={{ color: '#9e9e9e', fontSize: theme.iconSizes.small }} />}
                    </span>
                  </Tooltip>
                  <Typography variant="iconValueText" sx={{ textAlign: 'center', color: getSelectedChamber() ? getAlertColor(getSelectedChamber()?.chamber_alert || 'NO') : '#9e9e9e' }}>
                    {getSelectedChamber() ? (getSelectedChamber()?.chamber_alert || 'NO') : 'N/A'}
                  </Typography>
                  <Typography variant="iconLabelText" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    ALERT
                  </Typography>
                </Box>
                
                {/* Delete - always visible, but only clickable when DISCONNECTED */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 0,
                  p: 0,
                  borderRadius: 1
                }}>
                  <DeleteButton
                    onDelete={() => useChamberStore.getState().deleteChamber(getSelectedChamber()?.chamber_id || '')}
                    entityType="chamber"
                    entityId={getSelectedChamber()?.chamber_id || ''}
                    disabled={getSelectedChamber()?.chamber_status !== 'DISCONNECTED'}
                  />
                </Box>
              </Box>
              
              {/* Alert Description - shown when alert icon is clicked */}
              {showAlertDescription && getSelectedChamber()?.chamber_alert_description && (
                <Box sx={{ 
                  mt: 1, 
                  p: 1, 
                  backgroundColor: '#fff3e0', 
                  border: '1px solid #ffe0b2',
                  borderRadius: 1,
                  borderLeft: '4px solid #ff9800'
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      typography: 'alertLabel',
                      color: '#e65100',
                      fontWeight: 'medium',
                      display: 'block'
                    }}
                  >
                    Alert: {getSelectedChamber()?.chamber_alert_description}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Chamber Details Box */}
          <Box sx={{ 
            gridArea: 'chamber_details',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"DETAILS"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Arduino Port, Hardware, and Version in same row */}
              <Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
                {/* Arduino Port */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={getSelectedChamber()?.chamber_arduino_port || 'Unknown'}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '8px 8px'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    ARDUINO PORT
                  </Typography>
                </Box>
                
                {/* Arduino Hardware */}
                <Box sx={theme.fieldLabelContainer}>
                  <TextField
                    value={getSelectedChamber()?.arduino_hardware || 'Unknown'}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'left',
                          fontSize: theme.typography.fieldText.fontSize,
                          fontWeight: 'bold',
                          padding: '8px 8px',
                          color: getSelectedChamber()?.arduino_hardware === 'Gen2' ? '#4caf50' : 
                                 getSelectedChamber()?.arduino_hardware === 'Gen1' ? '#ff9800' : '#9e9e9e'
                        }
                      }
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: getSelectedChamber()?.arduino_hardware === 'Gen2' ? '#4caf50' : 
                                      getSelectedChamber()?.arduino_hardware === 'Gen1' ? '#ff9800' : '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: getSelectedChamber()?.arduino_hardware === 'Gen2' ? '#45a049' : 
                                      getSelectedChamber()?.arduino_hardware === 'Gen1' ? '#f57c00' : '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: getSelectedChamber()?.arduino_hardware === 'Gen2' ? '#4caf50' : 
                                      getSelectedChamber()?.arduino_hardware === 'Gen1' ? '#ff9800' : '#1976d2',
                        },
                      },
                    }}
                  />
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    ARDUINO HW
                  </Typography>
                </Box>
                
                {/* Version */}
                <Box sx={theme.fieldLabelContainer}>
                  <Tooltip 
                    title={
                      getSelectedChamber()?.chamber_software_version 
                        ? (isLatestVersion 
                            ? "Latest available version" 
                            : "Update Available")
                        : "No chamber connected"
                    } 
                    arrow
                  >
                    <TextField
                      value={getSelectedChamber()?.chamber_software_version || 'Unknown'}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        readOnly: true,
                        sx: {
                          '& .MuiInputBase-input': {
                            textAlign: 'left',
                            fontSize: theme.typography.fieldText.fontSize,
                            fontWeight: 'bold',
                            padding: '8px 8px',
                            color: getSelectedChamber()?.chamber_software_version 
                              ? (isLatestVersion ? '#4caf50' : '#f44336') 
                              : '#9e9e9e'
                          }
                        }
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: getSelectedChamber()?.chamber_software_version 
                              ? (isLatestVersion ? '#4caf50' : '#f44336') 
                              : '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: getSelectedChamber()?.chamber_software_version 
                              ? (isLatestVersion ? '#45a049' : '#d32f2f') 
                              : '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: getSelectedChamber()?.chamber_software_version 
                              ? (isLatestVersion ? '#4caf50' : '#f44336') 
                              : '#1976d2',
                          },
                        },
                      }}
                    />
                  </Tooltip>
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    VERSION
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Chamber Control Box */}
          <Box sx={{ 
            gridArea: 'chamber_control',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"CONTROLS"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Row 1: Pump Controls */}
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                {/* Pump Activation Time Container */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, position: 'relative', gap: 1, paddingBottom: '2px' }}>
                  {/* Down Arrow - Left Side */}
                  <IconButton 
                    size="small" 
                    sx={{ p: 0.5 }}
                    onClick={handlePumpTimeDecrement}
                  >
                    <KeyboardArrowDown fontSize="small" />
                  </IconButton>
                  
                  {/* TextField - Center */}
                  <TextField
                    value={pumpTime}
                    onChange={handlePumpTimeInputChange}
                    onBlur={handlePumpTimeBlur}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      sx: {
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          fontSize: theme.typography.fieldText.fontSize,
                          padding: '8px 8px'
                        }
                      }
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                    inputProps={{
                      min: 50,
                      step: 50
                    }}
                  />
                  
                  {/* Up Arrow - Right Side */}
                  <IconButton 
                    size="small" 
                    sx={{ p: 0.5 }}
                    onClick={handlePumpTimeIncrement}
                  >
                    <KeyboardArrowUp fontSize="small" />
                  </IconButton>
                  
                  <Typography 
                    variant="fieldLabel"
                    sx={theme.fieldLabelPositioned}
                  >
                    PUMP ACTIVATION TIME (ms)
                  </Typography>
                </Box>
                
                {/* Pump Control Buttons Container */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Button
                    onClick={isViewer ? () => triggerUnauthorized() : () => handlePumpCommand('negative')}
                    disabled={!isViewer && isChamberActionInFlight}
                    variant="outlined"
                    size="small"
                    sx={{
                      typography: 'buttonLabel',
                      fontWeight: 'bold',
                      borderColor: '#9e9e9e',
                      color: '#9e9e9e',
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      '&:hover': {
                        backgroundColor: '#9e9e9e',
                        color: 'white',
                        borderColor: '#9e9e9e'
                      }
                    }}
                  >
                    <KeyboardArrowLeft />
                  </Button>
                  <Button
                    onClick={isViewer ? () => triggerUnauthorized() : () => handlePumpCommand('positive')}
                    disabled={!isViewer && isChamberActionInFlight}
                    variant="outlined"
                    size="small"
                    sx={{
                      typography: 'buttonLabel',
                      fontWeight: 'bold',
                      borderColor: '#2196f3',
                      color: '#2196f3',
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      '&:hover': {
                        backgroundColor: '#2196f3',
                        color: 'white',
                        borderColor: '#2196f3'
                      }
                    }}
                  >
                    <KeyboardArrowRight />
                  </Button>
                </Box>
              </Box>
              
              {/* Border Delimiter */}
              <Box sx={{ 
                borderTop: '1px solid #ddd',
                width: '100%'
              }} />
              
              {/* Row 2: Sensor Calibration */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}>
                {/* Calibration Buttons Container */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Button
                    onClick={isViewer ? () => triggerUnauthorized() : () => handleCalibrateSensors('reset')}
                    disabled={!isViewer && isChamberActionInFlight}
                    variant="outlined"
                    size="small"
                    startIcon={<ResetIcon />}
                    sx={{
                      typography: 'buttonLabel',
                      fontWeight: 'bold',
                      borderColor: '#f44336',
                      color: '#f44336',
                      '& .MuiButton-startIcon': {
                        marginRight: '2px',
                        display: 'flex',
                        alignItems: 'center'
                      },
                      '& .MuiButton-startIcon > *:nth-of-type(1)': {
                        fontSize: '1rem'
                      },
                      '&:hover': {
                        backgroundColor: '#f44336',
                        color: 'white',
                        borderColor: '#f44336'
                      }
                    }}
                  >
                    RESET
                  </Button>
                  <Button
                    onClick={isViewer ? () => triggerUnauthorized() : () => handleCalibrateSensors('offset')}
                    disabled={!isViewer && isChamberActionInFlight}
                    variant="outlined"
                    size="small"
                    startIcon={<OffsetIcon />}
                    sx={{
                      typography: 'buttonLabel',
                      fontWeight: 'bold',
                      borderColor: '#2196f3',
                      color: '#2196f3',
                      '& .MuiButton-startIcon': {
                        marginRight: '2px',
                        display: 'flex',
                        alignItems: 'center'
                      },
                      '& .MuiButton-startIcon > *:nth-of-type(1)': {
                        fontSize: '1rem'
                      },
                      '&:hover': {
                        backgroundColor: '#2196f3',
                        color: 'white',
                        borderColor: '#2196f3'
                      }
                    }}
                  >
                    OFFSET
                  </Button>
                </Box>
                
                {/* Label */}
                <Typography 
                  variant="fieldLabel"
                  sx={{ 
                    color: 'text.secondary',
                    textAlign: 'center'
                  }}
                >
                  SENSORS CALIBRATION
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Chamber Monitor Box */}
          <Box sx={{ 
            gridArea: 'chamber_monitor',
            border: '1px solid #ddd', 
            borderRadius: 1, 
            p: 1.5,
            backgroundColor: 'white',
            position: 'relative',
            '&::before': {
              content: '"MONITORING"',
              position: 'absolute',
              top: '-8px',
              left: '8px',
              backgroundColor: 'white',
              padding: '0 4px',
              fontSize: theme.typography.sectionHeader.fontSize,
              fontWeight: 'bold',
              color: 'text.secondary',
              zIndex: 1
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Progress Bar Container - same design as ARDUINO PORT */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', paddingBottom: '2px' }}>
                <Box sx={{ 
                  width: '100%',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  padding: '8px 8px',
                  backgroundColor: 'white',
                  '&:hover': {
                    borderColor: '#bbb',
                  },
                  '&.Mui-focused': {
                    borderColor: '#1976d2',
                  },
                }}>
                  <Box sx={{ 
                    width: '100%', 
                    height: 20, 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <Box sx={{ 
                      width: `${progressPercentage}%`, 
                      height: '100%', 
                      backgroundColor: '#4caf50',
                      transition: 'width 0.3s ease'
                    }} />
                    <Typography 
                      variant="sectionHeader"
                      sx={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontWeight: 'bold',
                        color: 'black',
                        textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                        zIndex: 1
                      }}
                    >
                      {progressText}
                    </Typography>
                  </Box>
                </Box>
                <Typography 
                  variant="sectionHeader"
                  sx={{ 
                    position: 'absolute',
                    top: '100%',
                    left: '8px',
                    marginTop: '0px',
                    color: 'text.secondary',
                    backgroundColor: 'white',
                    padding: '0 2px',
                    lineHeight: 1.2,
                    whiteSpace: 'normal'
                  }}
                >
                  {measurementsText}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
    
    {/* Reset Calibration Dialog */}
    <ResetCalibrationDialog
      open={showResetDialog}
      onClose={handleCancelReset}
      onConfirm={handleConfirmReset}
      chamber={getSelectedChamber()}
      isResetting={isResetting}
    />
    
    {/* Offset Calibration Dialog */}
    <OffsetCalibrationDialog
      open={showOffsetDialog}
      onClose={handleCancelOffset}
      onConfirm={handleConfirmOffset}
      chamber={getSelectedChamber()}
      isCalibrating={isCalibrating}
    />
    </>
  );
};

export default ChamberCard; 